/**
 * f13.2: the "Submission" card on a Submitted cycle's workflow page (Finance
 * and Admin). Everything shown is the server's record: who submitted and when,
 * the row count, the SHA-256 that identifies the handed-over file, and the
 * snapshot the figures came from. The download returns the exact stored bytes.
 */

import { useState } from 'react';
import { Check, Copy, Download, Loader2 } from 'lucide-react';

import type { Cycle } from '@/api/client';

import '@/styles/cycle-submission.css';

import { downloadSubmission } from './useCycleSubmission';

const COUNT = new Intl.NumberFormat('en-US');
const STAMP = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
  timeZoneName: 'short',
});

type Submission = NonNullable<Cycle['submission']>;

function HashValue({ hash }: { hash: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(hash).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <span className="submission-hash">
      <span className="mono" title={hash}>
        {hash.slice(0, 12)}…
      </span>
      <button
        type="button"
        className="icon-btn"
        onClick={copy}
        aria-label={copied ? 'SHA-256 copied' : 'Copy the full SHA-256'}
        title={copied ? 'Copied' : 'Copy the full SHA-256'}
      >
        {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
      </button>
    </span>
  );
}

function DownloadButton({ cycleId, submission }: { cycleId: string; submission: Submission }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const download = () => {
    setBusy(true);
    setProblem(null);
    downloadSubmission(cycleId, submission.file_name)
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'The file could not be downloaded.'),
      )
      .finally(() => setBusy(false));
  };
  return (
    <div className="submission-download">
      <button type="button" className="btn btn-secondary" onClick={download} disabled={busy} aria-busy={busy}>
        {busy ? <Loader2 className="spin" aria-hidden="true" /> : <Download aria-hidden="true" />} Download
        submission file
      </button>
      {problem && (
        <span className="hint submission-problem" role="alert">
          {problem}
        </span>
      )}
    </div>
  );
}

export function SubmissionCard({ cycle }: { cycle: Cycle }) {
  const submission = cycle.submission;
  if (cycle.state.code !== 'SUBMITTED' || !submission) return null;

  return (
    <div className="card submission-card" data-testid="submission-card">
      <div className="card-head">
        <h3>Submission</h3>
        <span className="hint">Upload this file to COSMA by hand</span>
      </div>
      <div className="card-body stack-sm">
        <dl className="dl cols-2">
          <div>
            <dt>Submitted by</dt>
            <dd>{submission.submitted_by.display_name}</dd>
          </div>
          <div>
            <dt>Submitted at</dt>
            <dd>{STAMP.format(new Date(submission.submitted_at))}</dd>
          </div>
          <div>
            <dt>Rows</dt>
            <dd className="mono">{COUNT.format(submission.row_count)}</dd>
          </div>
          <div>
            <dt>Snapshot</dt>
            <dd className="mono">#{submission.snapshot.sequence}</dd>
          </div>
          <div>
            <dt>SHA-256</dt>
            <dd>
              <HashValue hash={submission.content_sha256} />
            </dd>
          </div>
          <div>
            <dt>File</dt>
            <dd className="mono submission-file">{submission.file_name}</dd>
          </div>
        </dl>
        <DownloadButton cycleId={cycle.id} submission={submission} />
      </div>
    </div>
  );
}
